import { Input } from '@daily/shared/components/Input';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { MAIN_ROOM_IDENTIFIER } from './helpers/constants';
import { useRoomMapping } from './hooks/useRoomMapping';
import { useRooms } from './hooks/useRooms';

interface Props {
  participantIds: string[];
}

export const NumberOfRoomsField: React.FC<Props> = ({ participantIds }) => {
  const { t } = useTranslation();
  const { addRoom, deleteRoom, rooms } = useRooms();
  const { getRoomMappingByRoomId, updateRoomMapping } = useRoomMapping();
  const [hasError, setHasError] = useState(false);
  const amountOfRooms = rooms.length;
  const [inputValue, setInputValue] = useState(amountOfRooms.toString());

  /**
   * When amount of rooms change (e.g. via room deletion), update Input value.
   */
  useEffect(() => {
    setInputValue(amountOfRooms.toString());
  }, [amountOfRooms]);

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value);
    const newAmount = Number(ev.target.valueAsNumber);

    setHasError(false);

    if (isNaN(newAmount)) {
      return;
    }
    // deleting rooms
    if (newAmount < amountOfRooms) {
      for (let i = newAmount; i < amountOfRooms; i++) {
        const participants = getRoomMappingByRoomId(rooms[i]);
        participants?.map((participant) => {
          updateRoomMapping(participant, MAIN_ROOM_IDENTIFIER);
        });
        deleteRoom(rooms[i]);
      }
    } else {
      // adding rooms
      for (let i = 0; i < newAmount - amountOfRooms; i++) {
        addRoom(uuidv4());
      }
    }
  };
  const handleBlur = () => {
    setHasError(amountOfRooms === 0);
  };

  const minParticipantsPerRoom = Math.floor(
    participantIds.length / rooms.length
  );
  const maxParticipantsPerRoom = Math.ceil(
    participantIds.length / rooms.length
  );

  return (
    <Stack gap={4}>
      <Text El="label" htmlFor="rooms" variant="strong">
        {t('breakouts.numberOfRooms')}
      </Text>
      <Input
        type="number"
        id="rooms"
        inputMode="numeric"
        pattern="[0-9]*"
        value={inputValue}
        min="1"
        step="1"
        name="rooms"
        required
        error={hasError}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {hasError ? (
        <Text El="p" color="error" variant="small">
          {t('breakouts.errorRoomsMin')}
        </Text>
      ) : (
        <Text El="p" variant="small">
          {minParticipantsPerRoom === maxParticipantsPerRoom
            ? t('breakouts.peoplePerRoom', {
                count: minParticipantsPerRoom,
              })
            : t('breakouts.peoplePerRoomMinMax', {
                min: minParticipantsPerRoom,
                max: maxParticipantsPerRoom,
              })}
        </Text>
      )}
    </Stack>
  );
};
